import React, { useState, useRef, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, resolveImage, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { clockTime, formatMoney, initials } from "@/lib/format";
import { PageLoader } from "@/components/common";
import { ArrowLeft, Send, ImagePlus, ShieldCheck, X } from "lucide-react";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [staged, setStaged] = useState([]);
  const [viewerImage, setViewerImage] = useState(null);
  const scrollRef = useRef(null);

  const { data: conv, isLoading } = useQuery({ queryKey: ["conversation", id], queryFn: () => apiFetch(`/conversations/${id}`) });
  const { data: messages } = useQuery({
    queryKey: ["messages", id],
    queryFn: () => apiFetch(`/conversations/${id}/messages`),
    refetchInterval: 6000,
  });

  useEffect(() => { apiFetch("/notices/read?type=message", { method: "POST" }).catch(() => {}); }, [id]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages]);

  const sendMessage = async () => {
    const t = text.trim();
    const photos = staged;
    if (!t && photos.length === 0) return;
    setText("");
    setStaged([]);
    setSending(true);
    try {
      for (const p of photos) {
        const url = await uploadImage(p.file);
        await apiFetch(`/conversations/${id}/messages`, { method: "POST", body: JSON.stringify({ image: url }) });
      }
      if (t) await apiFetch(`/conversations/${id}/messages`, { method: "POST", body: JSON.stringify({ text: t }) });
      qc.invalidateQueries({ queryKey: ["messages", id] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const onPickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setStaged((s) => [...s, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))].slice(0, 8));
    }
    e.target.value = "";
  };

  const removeStaged = (idx) => {
    setStaged((s) => {
      URL.revokeObjectURL(s[idx]?.url);
      return s.filter((_, i) => i !== idx);
    });
  };

  if (isLoading) return <PageLoader />;

  const other = conv?.other_user;
  const listing = conv?.listing;
  const isAdminThread = conv?.admin_thread;

  return (
    <div className="mx-auto flex h-[calc(100vh-4rem)] max-w-2xl flex-col px-4 sm:px-6">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border py-3">
        <button onClick={() => navigate("/messages")} className="rounded-full p-1.5 hover:bg-secondary" data-testid="chat-back"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-brand text-sm font-bold text-white">
          {isAdminThread ? "VT" : initials(other?.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 font-semibold">{isAdminThread ? (conv.subject || "Venda Team") : (other?.name || "User")}{other?.verified && <ShieldCheck className="h-4 w-4 text-sky-600" />}</div>
          {other?.phone && !isAdminThread && <a href={`tel:${other.phone}`} className="text-xs text-price">{other.phone}</a>}
        </div>
      </div>

      {/* Product card */}
      {listing && (
        <Link to={`/listing/${listing.id}`} className="mt-3 flex items-center gap-3 rounded-xl border border-border p-2" data-testid="chat-product-card">
          <div className="h-12 w-12 overflow-hidden rounded-lg bg-secondary">
            {listing.images?.[0] && <img src={resolveImage(listing.images[0])} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{listing.title}</p>
            <p className="text-sm font-bold text-price">{formatMoney(listing.price, listing.currency)}</p>
          </div>
        </Link>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto py-4" data-testid="chat-messages">
        {messages?.map((m) => {
          const mine = m.sender_id === user?.id;
          if (m.system) return <div key={m.id} className="text-center"><span className="rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground">{m.text}</span></div>;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-price text-white" : "bg-secondary text-foreground"}`}>
                {m.image && (
                  <button type="button" onClick={() => setViewerImage(resolveImage(m.image))} className="block cursor-zoom-in" data-testid={`chat-image-${m.id}`}>
                    <img src={resolveImage(m.image)} alt="" className="mb-1 max-h-60 rounded-lg" />
                  </button>
                )}
                {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                <p className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>{clockTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      {staged.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-border pt-3" data-testid="chat-staged-tray">
          {staged.map((p, i) => (
            <div key={p.url} className="relative h-16 w-16 overflow-hidden rounded-lg border border-border">
              <img src={p.url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeStaged(i)}
                className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/65 text-white hover:bg-black/85"
                data-testid={`chat-staged-remove-${i}`}
                aria-label="Remove photo"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 border-t border-border py-3">
        <label className="cursor-pointer rounded-full p-2 text-muted-foreground hover:bg-secondary" data-testid="chat-photo">
          <ImagePlus className="h-5 w-5" />
          <input type="file" accept="image/*" multiple hidden onChange={onPickPhotos} />
        </label>
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Type a message…"
          className="flex-1 rounded-full border border-input bg-white px-4 py-2.5 text-sm outline-none focus:border-price"
        />
        <button data-testid="chat-send" onClick={sendMessage} disabled={sending || (!text.trim() && staged.length === 0)} className="flex h-11 w-11 items-center justify-center rounded-full bg-price text-white disabled:opacity-50">
          <Send className="h-5 w-5" />
        </button>
      </div>

      {viewerImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setViewerImage(null)} data-testid="chat-image-viewer">
          <button onClick={() => setViewerImage(null)} className="absolute right-4 top-4 rounded-full bg-black/60 p-2.5 text-white ring-1 ring-white/50 transition hover:bg-black/80" data-testid="chat-image-close" aria-label="Close">
            <X className="h-6 w-6" />
          </button>
          <img src={viewerImage} alt="" onClick={(e) => e.stopPropagation()} className="max-h-[90vh] max-w-[92vw] object-contain" />
        </div>
      )}
    </div>
  );
}
